import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { usePresence } from "./presence-context";
import { usePresenceWithCursors } from "./use-cursors";
import { Pointer, cursorSVGs } from "./cursor";
import { readKey, readCleartextMessage, verify } from 'openpgp';
import { sharedStyles, isLightColor, getContrastingTextColor, VerificationIcon, SimpleVerificationIcon, isVerifiedDomain } from "./shared-styles";

// Main Chat Component
type ChatProps = {
  isQuietMode: boolean;
  toggleQuietMode: () => void;
  isChatEnabled?: boolean;
};

type ChatState = {
  listening: boolean;
  message: string;
  showCursor: boolean;
  isMovingRight: boolean;
  showInput: boolean;
  shouldPersist: boolean;
  isConnected: boolean;
  showPGPModal: boolean;
  pgpChallenge: string;
  currentAuthDomain: string;
  signedMessage: string;
};

type Cursor = {
  x: number;
  y: number;
};

// Chat Constants
const SHORTCUTS: [string, string][] = [
  ['/@@', '(@ _ @;)'],
  ['/\\?\\?', '¯\\_(ツ)_/¯'],
  ['/cat', 'ฅ^ • ﻌ • ^ฅ'],
  ['/cry', '｡°(°.◜ᯅ◝°)°｡'],
  ['/1337', '(⌐■_■)'],
];

const MUSICAL_NOTES = [440, 494.88, 523.25, 587.33, 659.25, 698.46, 783.99];

// Utility functions
const playBeep = () => {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'triangle';
  osc.frequency.setValueAtTime(
    MUSICAL_NOTES[Math.floor(Math.random() * MUSICAL_NOTES.length)],
    ctx.currentTime
  );

  gain.gain.setValueAtTime(0.06, ctx.currentTime);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.03);
};

const applyTextReplacements = (text: string): string => {
  return SHORTCUTS.reduce((result, [shortcut, replacement]) => 
    result.replace(new RegExp(shortcut, 'g'), replacement), text);
};

// Styles
const styles = {
  input: {
    boxSizing: "border-box",
    padding: 0,
    margin: 0,
    fontSize: "16px",
    lineHeight: 1,
    whiteSpace: "nowrap" as const,
  },
  chatBubbleContainer: {
    position: "absolute",
    zIndex: 300,
    pointerEvents: 'none' as const,
  },
  controlButtonsContainer: {
    position: 'fixed',
    bottom: '24px',
    right: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    zIndex: 301,
  },
  domainlogin: {
    position: 'relative',
    width: '44px',
    height: '44px',
  },
} as const;

// UI
const ColorPicker = React.memo(({ color, onChange }: { color: string; onChange: (color: string) => void }) => (
  <div style={sharedStyles.colorButton}>
    <input
      type="color"
      value={color}
      onClick={playBeep}
      onChange={(e) => onChange(e.target.value)}
      style={sharedStyles.colorInput}
    />
  </div>
));

const UserIcon = React.memo(() => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
  </svg>
));

const EyeIcon = React.memo(({ isOpen }: { isOpen: boolean }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    {isOpen ? (
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    ) : (
      <path d="M3 12h18" strokeWidth="2" stroke="currentColor" fill="none"/>
    )}
  </svg>
));

// PGP
type PGPAuthModalProps = {
  isOpen: boolean;
  onClose: () => void;
  challenge: string;
  onVerify: (signedMessage: string) => void;
  signedMessage: string;
  setSignedMessage: React.Dispatch<React.SetStateAction<string>>;
};

// PGP operations
const fetchWithProxy = async (url: string, accept: string = 'text/html') => {
  const corsProxy = 'https://proxy.ellesho.me/?';
  const encodedUrl = encodeURIComponent(url);
  const fullUrl = `${corsProxy}url=${encodedUrl}`;
  
  const response = await fetch(fullUrl, {
    headers: { 'Accept': accept }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  
  return response.text();
};

const extractPGPKeyUrl = (html: string) => {
  return html.match(/<link[^>]*rel="[^"]*pgpkey[^"]*"[^>]*href="([^"]*\.(?:pub|gpg|asc))"/)?.[1] ||
         html.match(/<meta[^>]*name="pgpkey"[^>]*content="([^"]*)"/)?.[1];
};

const getAbsoluteUrl = (url: string, domain: string) => {
  return url.startsWith('http') ? url : `https://${domain}${url.startsWith('/') ? '' : '/'}${url}`;
};

// PGP Modal Component
const PGPAuthModal = React.memo(({ 
  isOpen, 
  onClose, 
  challenge, 
  onVerify,
  signedMessage,
  setSignedMessage
}: PGPAuthModalProps) => {
  const challengeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const myself = usePresence(state => state.myself);
  const myId = usePresence(state => state.myId);
  const svgIndex = myself?.presence.cursorIndex ?? (myId?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) ?? 0) % 5;
  const currentColor = myself?.presence.color ?? "#000000";

  useEffect(() => {
    if (isOpen && challengeTextareaRef.current) {
      challengeTextareaRef.current.select();
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleVerify = async (signedMessage: string) => {
    await onVerify(signedMessage);
    handleClose();
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      <style>
        {`
          @keyframes blurIn {
            from { backdrop-filter: blur(0); -webkit-backdrop-filter: blur(0); background-color: rgba(255, 255, 255, 0); }
            to { backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); background-color: rgba(255, 255, 255, 0.1); }
          }
          @keyframes blurOut {
            from { backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); background-color: rgba(255, 255, 255, 0.1); }
            to { backdrop-filter: blur(0); -webkit-backdrop-filter: blur(0); background-color: rgba(255, 255, 255, 0); }
          }
          @keyframes scaleIn {
            from { transform: scale(0); }
            70% { transform: scale(1.05); }
            to { transform: scale(1); }
          }
          @keyframes scaleOut {
            from { transform: scale(1); }
            30% { transform: scale(1.05); }
            to { transform: scale(0); }
          }
          @keyframes growUp {
            from { transform: translateX(-50%) scaleY(0); }
            to { transform: translateX(-50%) scaleY(1); }
          }
          .modal-button:active {
            background: rgba(0, 0, 0, 0.1) !important;
          }
        `}
      </style>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'sans-serif',
        fontSize: '1rem',
        zIndex: 700,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        isolation: 'isolate',
        animation: isClosing ? 'blurOut 0.3s ease forwards' : 'blurIn 0.3s ease forwards',
        opacity: 1,
      }} onClick={handleClose}>
        <div style={{
          backgroundColor: 'rgba(238, 238, 238, 0.7)',
          padding: '24px',
          borderRadius: '50px',
          maxWidth: '232px',
          width: '80%',
          border: '1px solid #fff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          animation: isClosing ? 'scaleOut 0.3s ease forwards' : 'scaleIn 0.3s ease forwards',
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', width: '232px', top: '-20px' }}>
            <div style={{ transform: 'scaleX(-1)' }}>
              <Pointer fill={currentColor} svgIndex={svgIndex} />
            </div>
          </div>
          <p style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>To verify and use your domain name in chat, please sign this message with your PGP key:</p>
            <div style={{ position: 'relative' }}>
              <textarea
                ref={challengeTextareaRef}
                value={challenge}
                readOnly={false}
                onKeyDown={(e) => {
                  if (e.key !== 'a' && e.key !== 'c' && e.key !== 'v' && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  if (e.target.value.includes('-----BEGIN PGP SIGNED MESSAGE-----')) {
                    setSignedMessage(e.target.value);
                  }
                }}
                style={sharedStyles.textarea}
              />
            </div>
            <p style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>and paste the signed text here:</p>
            <textarea
              value={signedMessage}
              onChange={(e) => setSignedMessage(e.target.value)}
              placeholder="-----BEGIN PGP SIGNED MESSAGE-----"
              style={sharedStyles.textarea}
            />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
          }}>
            <button
              onClick={handleClose}
              style={sharedStyles.modalButton}
              className="modal-button"
            >
              Cancel
            </button>
            <button
              onClick={() => handleVerify(signedMessage)}
              style={sharedStyles.modalButton}
              className="modal-button"
            >
              Verify
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

// Main Chat component
export default function Chat({ isQuietMode, toggleQuietMode, isChatEnabled = true }: ChatProps) {
  // State
  const [chatState, setChatState] = useState<ChatState>({
    listening: false,
    message: "",
    showCursor: true,
    isMovingRight: true,
    showInput: false,
    shouldPersist: false,
    isConnected: !!localStorage.getItem('authenticated_domain'),
    showPGPModal: false,
    pgpChallenge: '',
    currentAuthDomain: '',
    signedMessage: '',
  });

  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const prevCursorRef = useRef<Cursor | null>(null);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [showCursorMenu, setShowCursorMenu] = useState(false);
  const cursorMenuRef = useRef<HTMLDivElement>(null);
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Hooks
  const updatePresence = usePresence(state => state.updatePresence);
  const myself = usePresence(state => state.myself);
  const myId = usePresence(state => state.myId);
  const showCTA = usePresence(state => 
    Array.from(state.otherUsers.values()).some(user => user.presence?.message)
  );
  const myCursor = usePresenceWithCursors(state => state.myself?.presence.cursor);

  // Derived values
  const svgIndex = myself?.presence.cursorIndex ?? (myId?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) ?? 0) % 5;
  const currentColor = myself?.presence.color ?? "#000000";
  const isLight = isLightColor(currentColor);

  // Effects
  useEffect(() => {
    if (myCursor && prevCursorRef.current) {
      if (myCursor.x !== prevCursorRef.current.x) {
        setChatState(prev => ({ ...prev, isMovingRight: myCursor.x > prevCursorRef.current!.x }));
      }
    }
    if (myCursor) {
      prevCursorRef.current = { x: myCursor.x, y: myCursor.y };
    }
  }, [myCursor]);

  useEffect(() => {
    const interval = setInterval(() => {
      setChatState(prev => ({ ...prev, showCursor: !prev.showCursor }));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    updatePresence({ 
      message: chatState.message.length > 0 ? chatState.message : null,
      domain: myself?.presence.domain || localStorage.getItem('authenticated_domain'),
    });
  }, [chatState.message, updatePresence, myself?.presence.domain]);

  useEffect(() => {
    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      const newTimeoutId = setTimeout(() => {
        if (!chatState.shouldPersist) {
          setChatState(prev => ({
            ...prev,
            listening: false,
            message: "",
            showInput: false,
          }));
        } else {
          setChatState(prev => ({ ...prev, shouldPersist: false }));
        }
      }, chatState.shouldPersist ? 150000 : 10000);

      setTimeoutId(newTimeoutId);
    };

    if (chatState.message || chatState.listening) {
      resetTimeout();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [chatState.message, chatState.listening, chatState.shouldPersist]);

  const resetMenuTimeout = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    menuTimeoutRef.current = setTimeout(() => {
      setShowCursorMenu(false);
    }, 3000);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cursorMenuRef.current && !cursorMenuRef.current.contains(event.target as Node)) {
        setShowCursorMenu(false);
      }
    };

    if (showCursorMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      resetMenuTimeout();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, [showCursorMenu]);

  useEffect(() => {
    // On mount, if we have an authenticated domain and it's not set in presence, set it
    const storedDomain = localStorage.getItem('authenticated_domain');
    if (storedDomain && (!myself?.presence.domain || myself.presence.domain !== storedDomain)) {
      updatePresence({ domain: storedDomain });
    }
    // Only run on mount or when myself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myself]);

  // Event handlers
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isChatEnabled) {
      // If chat is disabled, don't do anything
      return;
    }

    const key = event.key.toLowerCase();
    const isModifierKey = event.metaKey || event.ctrlKey || event.altKey;

    if (!chatState.listening) {
      if (key === "/") {
        setChatState(prev => ({
          ...prev,
          message: "",
          listening: true,
          showInput: true,
          shouldPersist: false,
        }));
        event.preventDefault();
      } else if (key === "escape" && chatState.message) {
        setChatState(prev => ({
          ...prev,
          message: "",
          showInput: false,
          shouldPersist: false,
        }));
        event.preventDefault();
      }
    } else {
      if (!isModifierKey) {
        if (key === "enter") {
          setChatState(prev => ({
            ...prev,
            listening: false,
            shouldPersist: true,
          }));
        } else if (key === "escape") {
          setChatState(prev => ({
            ...prev,
            listening: false,
            message: "",
            showInput: false,
            shouldPersist: false,
          }));
        } else if (key === "backspace") {
          setChatState(prev => ({
            ...prev,
            message: prev.message.slice(0, -1),
          }));
        } else if (key.length === 1) {
          setChatState(prev => ({
            ...prev,
            message: applyTextReplacements(prev.message + key).slice(0, 64),
          }));
        }

        event.preventDefault();
        event.stopPropagation();
      }
    }
  }, [chatState.listening, chatState.message, isChatEnabled]);

  useEffect(() => {
    if (isChatEnabled) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [handleKeyDown, isChatEnabled]);

  // PGP Authentication handlers
  const handlePGPAuth = async (domain: string) => {
    try {
      const html = await fetchWithProxy(`https://${domain}`);
      const pgpKeyUrl = extractPGPKeyUrl(html);
      
      if (!pgpKeyUrl) {
        throw new Error('No PGP public key found. Please add your PGP key to your website using either:\n\n' +
                      '1. A link tag:\n\n <link rel="pgpkey" href="path/to/key.pub/.gpg/.asc">\n\n' +
                      '2. A meta tag:\n\n <meta name="pgpkey" content="your-key">');
      }

      const absoluteUrl = getAbsoluteUrl(pgpKeyUrl, domain);
      const pgpKey = await fetchWithProxy(absoluteUrl, 'text/plain');
      
      if (!pgpKey.includes('-----BEGIN PGP PUBLIC KEY BLOCK-----')) {
        throw new Error('Invalid PGP key format');
      }

      const challenge = `Verify domain ownership for ${domain} at ${new Date().toISOString()}`;
      setChatState(prev => ({
        ...prev,
        pgpChallenge: challenge,
        currentAuthDomain: domain,
        showPGPModal: true,
        signedMessage: '',
      }));

    } catch (error) {
      console.error('PGP auth error:', error);
      alert((error instanceof Error ? error.message : String(error)));
    }
  };

  const handlePGPVerify = async (signedMessage: string) => {
    if (!signedMessage) {
      alert('Please provide a signed message');
      return;
    }

    try {
      const domain = chatState.currentAuthDomain;
      const html = await fetchWithProxy(`https://${domain}`);
      const pgpKeyUrl = extractPGPKeyUrl(html);
      
      if (!pgpKeyUrl) {
        throw new Error('No PGP public key found on this domain');
      }

      const absoluteUrl = getAbsoluteUrl(pgpKeyUrl, domain);
      const pgpKey = await fetchWithProxy(absoluteUrl, 'text/plain');
      
      if (!pgpKey.includes('-----BEGIN PGP PUBLIC KEY BLOCK-----')) {
        throw new Error('Invalid PGP key format');
      }

      const publicKey = await readKey({ armoredKey: pgpKey });
      const message = await readCleartextMessage({ cleartextMessage: signedMessage });
      const verificationResult = await verify({ message, verificationKeys: publicKey });

      const { verified } = verificationResult.signatures[0];
      await verified;

      const verifiedMessage = message.getText();
      if (verifiedMessage.trim() !== chatState.pgpChallenge.trim()) {
        throw new Error('Signed message does not match the challenge');
      }

      const keyUserIds = publicKey.users.map(user => user.userID?.email).filter(Boolean);
      const domainMatch = keyUserIds.some(email => email?.endsWith(`@${domain}`));
      
      if (!domainMatch) {
        throw new Error('The PGP key does not belong to this domain');
      }

      localStorage.setItem('authenticated_domain', domain);
      setTimeout(() => {
        updatePresence({ domain });
        setChatState(prev => ({ ...prev, isConnected: true }));
      }, 1000);

      setChatState(prev => ({
        ...prev,
        showPGPModal: false,
        signedMessage: '',
        pgpChallenge: '',
      }));

    } catch (error) {
      console.error('PGP verification error:', error);
      alert('Failed to verify signature: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleDomainAuth = async () => {
    try {
      const storedDomain = localStorage.getItem('authenticated_domain');
      if (storedDomain) {
        localStorage.removeItem('authenticated_domain');
        updatePresence({ 
          domain: null,
          name: myself?.presence.name || '',
          color: myself?.presence.color || '#000000',
          message: chatState.message.length > 0 ? chatState.message : null
        });
        setChatState(prev => ({ ...prev, isConnected: false }));
        return;
      }

      const userDomain = prompt('Enter your domain (e.g., example.com):');
      if (!userDomain) return;

      await handlePGPAuth(userDomain);

    } catch (error) {
      console.error('Domain auth error:', error);
      alert('Failed to start authentication: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleColorChange = (color: string) => {
    updatePresence({ color });
  };

  const handleCursorSelect = (index: number) => {
    updatePresence({ cursorIndex: index });
    setShowCursorMenu(false);
    playBeep();
  };

  const handleMenuToggle = () => {
    setShowCursorMenu(!showCursorMenu);
    playBeep();
  };

  const renderChatContent = () => {
    if (chatState.message) return chatState.message;
    if (chatState.listening) return chatState.showCursor ? "..." : "\u00A0".repeat(3);
    if (showCTA && !isQuietMode) return "type / to reply";
    return null;
  };

  if (!isChatEnabled) return null;

  return (
    <>
      {(chatState.showInput || chatState.message || (showCTA && !isQuietMode)) && myCursor && (
        <div
          style={{
            ...styles.chatBubbleContainer,
            left: `${myCursor.x}px`,
            top: `${myCursor.y}px`,
            pointerEvents: chatState.message || chatState.listening ? 'none' : 'none',
          }}
        >
          <div style={{ transform: chatState.isMovingRight ? 'scaleX(-1)' : 'none' }}>
            <div style={sharedStyles.cursorWrapper}>
              <Pointer fill={currentColor} svgIndex={svgIndex} />
            </div>
          </div>
          {myself?.presence.domain && (
            <div style={{
              ...sharedStyles.domainNameContainer,
              color: getContrastingTextColor(currentColor),
              backgroundColor: currentColor,
            }}>
              <span>{myself.presence.domain}</span>
              {isVerifiedDomain(myself.presence.domain) ? (
                <VerificationIcon isLight={isLight} color={currentColor} />
              ) : (
                <SimpleVerificationIcon isLight={isLight} color={currentColor} />
              )}
            </div>
          )}
          <div style={{ 
            ...sharedStyles.chatBubble,
            backgroundColor: currentColor,
            color: getContrastingTextColor(currentColor),
            pointerEvents: chatState.message || chatState.listening ? 'none' : 'none',
          }}>
            <div style={styles.input}>
              {renderChatContent()}
            </div>
          </div>
        </div>
      )}
      {(chatState.showInput || chatState.message) && (
        <div style={{
          ...styles.controlButtonsContainer,
          pointerEvents: 'auto',
        }}>
          <div style={{ position: 'relative' }}>
            <button
              style={{
                ...sharedStyles.button,
                ...sharedStyles.controlButton,
              }}
              onClick={handleMenuToggle}
            >
              <Pointer fill={currentColor} svgIndex={svgIndex} />
            </button>
            <div 
              ref={cursorMenuRef}
              onMouseMove={resetMenuTimeout}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: `translateX(-50%) scale(${showCursorMenu ? 1 : 0})`,
                marginBottom: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                transformOrigin: 'bottom center',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out',
                opacity: showCursorMenu ? 1 : 0,
                pointerEvents: showCursorMenu ? 'auto' : 'none',
                visibility: 'visible',
              }}>
              {cursorSVGs.map((_: React.ReactElement, index: number) => (
                <button
                  key={index}
                  style={{
                    ...sharedStyles.button,
                    ...sharedStyles.controlButton,
                    width: '36px',
                    height: '36px',
                  }}
                  onClick={() => handleCursorSelect(index)}
                >
                  <Pointer fill={svgIndex === index ? currentColor : '#ccc'} svgIndex={index} />
                </button>
              ))}
            </div>
          </div>
          <div style={sharedStyles.colorPickerContainer}>
            <ColorPicker
              color={currentColor}
              onChange={handleColorChange}
            />
          </div>
          <button
            style={{
              ...sharedStyles.button,
              ...sharedStyles.controlButton,
              backgroundColor: isQuietMode ? '#000' : 'rgba(245, 245, 245, 0.7)',
            }}
            onClick={() => {
              toggleQuietMode();
              playBeep();
            }}
          >
            <EyeIcon isOpen={!isQuietMode} />
          </button>
          <div style={styles.domainlogin}>
            <button 
              style={{
                ...sharedStyles.button,
                ...sharedStyles.controlButton,
                backgroundColor: chatState.isConnected ? '#000' : 'rgba(238, 238, 238, 0.7)',
              }} 
              onClick={() => {
                playBeep();
                setTimeout(() => {
                  handleDomainAuth();
                }, 60);
              }}
            >
              <UserIcon />
            </button>
          </div>
        </div>
      )}
      <PGPAuthModal
        isOpen={chatState.showPGPModal}
        onClose={() => setChatState(prev => ({ ...prev, showPGPModal: false }))}
        challenge={chatState.pgpChallenge}
        onVerify={handlePGPVerify}
        signedMessage={chatState.signedMessage}
        setSignedMessage={(msg) => {
          if (typeof msg === 'function') {
            setChatState(prev => ({ ...prev, signedMessage: msg(prev.signedMessage) }));
          } else {
            setChatState(prev => ({ ...prev, signedMessage: msg }));
          }
        }}
      />
    </>
  );
}